import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BookOpenCheck, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";

const TERMS = ["estágio", "estagio", "estagiário", "estagiaria", "aprendiz", "jovem aprendiz"];

function isInternship(vaga) {
  const text = `${vaga.cargo || ""} ${vaga.tipo || ""} ${vaga.descricao || ""}`.toLowerCase();
  return TERMS.some((term) => text.includes(term));
}

export default function Estagios() {
  const [vagas, setVagas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cidade, setCidade] = useState("");

  useEffect(() => {
    api.get("/vagas")
      .then((r) => setVagas(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const cidades = useMemo(() => [...new Set(vagas.map((v) => v.cidade).filter(Boolean))].sort(), [vagas]);
  const oportunidades = useMemo(
    () => vagas.filter((v) => isInternship(v) && (!cidade || v.cidade === cidade)),
    [vagas, cidade]
  );

  return (
    <main className="max-w-7xl mx-auto px-5 py-10 md:py-14">
      <section className="rounded-3xl bg-gradient-to-br from-emerald-600 to-teal-800 text-white p-7 md:p-10">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 text-emerald-100 font-extrabold"><BookOpenCheck size={20} /> Estágio Limpo</span>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight mt-3">Estágio e aprendizagem com informação clara</h1>
          <p className="text-emerald-50 text-lg mt-4 leading-relaxed">
            Esta área separa anúncios que mencionam estágio, jovem aprendiz ou aprendizagem. Leia a vaga inteira e confirme bolsa, jornada, atividades, local e requisitos antes de enviar seus dados.
          </p>
        </div>
      </section>

      <section className="grid md:grid-cols-4 gap-4 mt-8">
        {["Bolsa ou remuneração", "Carga horária", "Atividades reais", "Canal de candidatura"].map((item) => (
          <div key={item} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start gap-3">
            <CheckCircle2 className="text-emerald-600 shrink-0" size={21} />
            <div>
              <h2 className="font-heading font-extrabold">{item}</h2>
              <p className="text-slate-500 text-sm mt-1">Confira essa informação antes de avançar.</p>
            </div>
          </div>
        ))}
      </section>

      <div className="mt-8 bg-amber-50 border border-amber-200 rounded-2xl p-5 flex gap-3 text-amber-950">
        <AlertCircle className="shrink-0 mt-0.5" size={22} />
        <p><strong>Importante:</strong> a classificação é feita pelo texto do anúncio. A plataforma não substitui a conferência dos requisitos, da instituição concedente ou das condições do estágio.</p>
      </div>

      <section className="mt-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <h2 className="font-heading text-3xl font-extrabold tracking-tight">Oportunidades identificadas</h2>
            <p className="text-slate-600 mt-1">Estágios, jovem aprendiz e vagas de aprendizagem encontradas nos anúncios ativos.</p>
          </div>
          <select value={cidade} onChange={(e) => setCidade(e.target.value)} className="bg-white border border-slate-200 rounded-xl px-4 py-3 min-w-56">
            <option value="">Todas as cidades</option>
            {cidades.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center text-slate-500"><Loader2 className="animate-spin" /></div>
        ) : oportunidades.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-3xl p-8 text-center">
            <h3 className="font-heading text-xl font-extrabold">Nenhum anúncio identificado nesta categoria agora.</h3>
            <p className="text-slate-600 mt-2">As vagas continuam disponíveis na busca geral.</p>
            <Link to="/" className="inline-block mt-5 bg-slate-950 text-white font-extrabold px-5 py-3 rounded-xl">Ver todas as vagas</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {oportunidades.map((vaga) => (
              <Link key={vaga.id} to={`/vaga/${vaga.id}`} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all">
                <span className="text-xs font-extrabold bg-emerald-100 text-emerald-800 rounded-full px-3 py-1">Estágio / aprendizagem</span>
                <h3 className="font-heading text-xl font-extrabold mt-4">{vaga.cargo}</h3>
                <p className="text-slate-500 font-bold mt-1">{vaga.company_name} • {vaga.cidade}</p>
                <p className="text-slate-600 mt-4 line-clamp-3">{vaga.descricao}</p>
                <p className="font-extrabold mt-4">Remuneração: {vaga.salario || "não informada"}</p>
                <span className="inline-block mt-4 text-emerald-700 font-extrabold">Abrir oportunidade →</span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
